import { Clock3, QrCode, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "../../app/router";
import { formatBytes } from "../../packaging";
import { QR_PROTOCOL_VERSION, shortSessionCode } from "../../protocol";
import { QrSessionStore, type QrSessionRecord } from "./receiverProgressStore";
import "./transfer.css";

function statusLabel(record: QrSessionRecord): string {
  if (record.role === "sender") {
    return record.status === "prepared" ? "Ready to send" : "Paused sender";
  }
  switch (record.status) {
    case "verified":
      return "Verified";
    case "conflict":
      return "Needs restart";
    case "receiving":
    case "paused":
      return "Paused receiver";
  }
}

function progressLabel(record: QrSessionRecord): string {
  if (record.role === "sender") {
    return `${record.frameCount.toLocaleString()} frames · ${record.fileCount.toLocaleString()} ${
      record.fileCount === 1 ? "file" : "files"
    }`;
  }
  return `${record.receivedFrames.toLocaleString()} / ${
    record.frameCount?.toLocaleString() ?? "?"
  } frames saved`;
}

function resumePath(record: QrSessionRecord): string {
  return record.role === "sender"
    ? `/transfer?resume=${encodeURIComponent(record.sessionId)}`
    : `/receive?s=${encodeURIComponent(record.sessionId)}&v=${QR_PROTOCOL_VERSION.toString()}`;
}

export function QrSessionsPanel() {
  const store = useMemo(() => new QrSessionStore(), []);
  const closeTimerRef = useRef<number | undefined>(undefined);
  const [sessions, setSessions] = useState<QrSessionRecord[]>();
  const [message, setMessage] = useState<string>();

  useEffect(() => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
    let cancelled = false;
    void store
      .listSessions()
      .then((records) => {
        if (!cancelled) {
          setSessions(records);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSessions([]);
          setMessage(
            "Saved QR sessions could not be read. Check browser storage access.",
          );
        }
      });
    return () => {
      cancelled = true;
      closeTimerRef.current = window.setTimeout(() => {
        void store.close();
      }, 0);
    };
  }, [store]);

  async function deleteSession(record: QrSessionRecord): Promise<void> {
    const code = shortSessionCode(record.sessionId);
    if (
      !window.confirm(
        `Delete the local ${record.role} session ${code} and all of its saved QR frames? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await store.deleteSession(record.role, record.sessionId);
      setSessions((current) =>
        current?.filter(
          (candidate) =>
            candidate.role !== record.role || candidate.sessionId !== record.sessionId,
        ),
      );
      setMessage(`Local session ${code} was deleted.`);
    } catch {
      setMessage(`Local session ${code} could not be deleted.`);
    }
  }

  return (
    <section aria-labelledby="qr-sessions-title" className="qr-sessions">
      <div className="qr-sessions__header">
        <div>
          <p className="eyebrow">This device</p>
          <h2 id="qr-sessions-title">Resume QR sessions</h2>
          <p>
            Reopen a sender or receiver after pausing, navigating away, or reloading.
            Prepared senders reopen at the invitation. Senders that had started reopen
            paused at the beginning of the saved transfer.
          </p>
        </div>
        <QrCode aria-hidden="true" />
      </div>

      {sessions === undefined ? (
        <p className="qr-sessions__empty">Checking saved QR sessions…</p>
      ) : sessions.length === 0 ? (
        <p className="qr-sessions__empty">
          No saved QR sessions yet. Prepared senders and invited receivers will appear
          here automatically.
        </p>
      ) : (
        <ul className="qr-sessions__list">
          {sessions.map((record) => (
            <li key={`${record.role}-${record.sessionId}`}>
              <div className="qr-sessions__main">
                <div className="qr-sessions__title-row">
                  <span>{record.role === "sender" ? "Sent" : "Received"}</span>
                  <strong>{record.displayName}</strong>
                </div>
                <div className="qr-sessions__meta">
                  <span>Session {shortSessionCode(record.sessionId)}</span>
                  <span>{statusLabel(record)}</span>
                  <span>{progressLabel(record)}</span>
                  <span>{formatBytes(record.storedBytes)} local</span>
                </div>
                <small>
                  <Clock3 aria-hidden="true" size={14} />
                  Last activity {new Date(record.updatedAt).toLocaleString()}
                </small>
              </div>
              <div className="qr-sessions__actions">
                {record.role === "receiver" && record.status === "conflict" ? null : (
                  <button
                    className="button button-primary"
                    onClick={() => {
                      navigate(resumePath(record));
                    }}
                    type="button"
                  >
                    {record.role === "sender" ? "Resume sending" : "Continue receiving"}
                  </button>
                )}
                <button
                  aria-label={`Delete ${record.role} session ${shortSessionCode(record.sessionId)}`}
                  className="button button-secondary"
                  onClick={() => {
                    void deleteSession(record);
                  }}
                  type="button"
                >
                  <Trash2 aria-hidden="true" size={17} />
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {message === undefined ? null : (
        <p aria-live="polite" className="status-banner">
          {message}
        </p>
      )}
    </section>
  );
}
